import { useEffect, useMemo, useRef } from "react"
import { notification } from "components/common"
import { fieldMapper } from "../../../../../utils"
import { useLatest } from "./useLatest"

function getOcrSyncKey(paymentProof, ocrImageData) {
  if (!paymentProof || !ocrImageData) return ""
  const proofId =
    paymentProof?.url ||
    paymentProof?.name ||
    paymentProof?.fileList?.[0]?.uid ||
    paymentProof?.fileList?.[0]?.name ||
    ""
  const img = ocrImageData
  const ocrPart =
    img?.id ?? img?.chequeNumber ?? img?.referenceNumber ?? img?.transactionNumber ?? img?.payorderNumber ?? ""
  return `${proofId}|${ocrPart}`
}


function useFetchPaymentModeOnProof({ paymentProof, paymentModesMap, getPaymentMode }) {
  const getPaymentModeLatest = useLatest(getPaymentMode)
  const lastKeyRef = useRef(null)

  const identityKey = useMemo(() => {
    if (!paymentProof) return ""
    return paymentProof.url || paymentProof.fileList?.[0]?.uid || paymentProof.fileList?.[0]?.name || ""
  }, [paymentProof])

  useEffect(() => {
    if (!paymentProof) return
    const id = paymentModesMap?.[paymentProof?.attachmentType]?.meta?.id
    if (!id) return
    const dedupeKey = `${identityKey}|${id}`
    if (lastKeyRef.current === dedupeKey) return
    lastKeyRef.current = dedupeKey
    getPaymentModeLatest.current(id, true)
  }, [paymentModesMap, identityKey, paymentProof?.attachmentType, getPaymentModeLatest]) 
}

function useSyncOcrFieldsToRow({
  fullPath,
  form,
  paymentProof,
  ocrData,
  modeOfPayment,
  paymentModesMap,
  validateReference,
  getBankName,
}) {
  const apis = useLatest({ validateReference, getBankName })
  const lastChequeValidationKeyRef = useRef(null)
  const lastAppliedOcrSyncKeyRef = useRef(null)

  const ocrSyncKey = useMemo(
    () => getOcrSyncKey(paymentProof, ocrData?.imageData),
    [
      paymentProof?.url,
      paymentProof?.name,
      paymentProof?.fileList?.[0]?.uid,
      paymentProof?.fileList?.[0]?.name,
      ocrData?.imageData?.id,
      ocrData?.imageData?.chequeNumber,
      ocrData?.imageData?.referenceNumber,
      ocrData?.imageData?.transactionNumber,
      ocrData?.imageData?.payorderNumber,
    ]
  )

  useEffect(() => {
    if (!ocrData?.imageData) {
      lastAppliedOcrSyncKeyRef.current = null
      return
    }
    if (!paymentProof?.imageData?.paymentModeKey || !modeOfPayment) return
    if (!ocrSyncKey) return

    if (lastAppliedOcrSyncKeyRef.current === ocrSyncKey) return
    lastAppliedOcrSyncKeyRef.current = ocrSyncKey

    const { imageData } = ocrData
    const meta = paymentModesMap?.[modeOfPayment]?.meta
    const setScoped = values =>
      Object.keys(values || {}).forEach(key => form.setFieldValue([...fullPath, key], values[key]))
    const scopedForm = { setFieldsValue: setScoped }
    const { validateReference: validateRef, getBankName: fetchBanks } = apis.current

    const runChequeValidation = () => {
      if (!imageData?.chequeNumber) return
      const proofId =
        paymentProof?.url ||
        paymentProof?.name ||
        paymentProof?.fileList?.[0]?.uid ||
        paymentProof?.fileList?.[0]?.name ||
        ""
      const dedupeKey = `${proofId}|${imageData.chequeNumber}|${imageData?.Bank?.key ?? ""}|${modeOfPayment}`
      if (lastChequeValidationKeyRef.current === dedupeKey) return
      lastChequeValidationKeyRef.current = dedupeKey

      validateRef({
        referenceNumber: imageData.chequeNumber,
        bankKey: imageData?.Bank?.key,
        paymentModeKey: modeOfPayment,
      })
        .unwrap()
        .then(() => form.validateFields([[...fullPath, "referenceNumber"]]))
        .catch(e => notification.error({ message: e.message }))
    }

    if (modeOfPayment === "cheque") {
      fetchBanks({}, true)
      fieldMapper(imageData, meta?.cheque, scopedForm)
      runChequeValidation()
    } else if (modeOfPayment === "online_payment") {
      fetchBanks({}, true)
      fieldMapper(imageData, meta?.onlinePayment, scopedForm)
      form.setFieldValue([...fullPath, "paymentType"], imageData?.onlinePaymentType)
    } else if (modeOfPayment === "card_payment") {
      fieldMapper(imageData, meta?.cardPayment, scopedForm)
    } else if (modeOfPayment === "cash") {
      fetchBanks({}, true)
      fieldMapper(imageData, meta?.depositSlip, scopedForm)
    } else if (modeOfPayment === "bank_order") {
      fetchBanks({}, true)
    }
  }, [ocrSyncKey, modeOfPayment, paymentModesMap, form, fullPath, paymentProof?.imageData?.paymentModeKey, ocrData, apis])
}

export function usePaymentRowEffects(props) {
  useFetchPaymentModeOnProof({
    paymentProof: props.paymentProof,
    paymentModesMap: props.paymentModesMap,
    getPaymentMode: props.getPaymentMode,
  })
  useSyncOcrFieldsToRow({
    fullPath: props.fullPath,
    form: props.form,
    paymentProof: props.paymentProof,
    ocrData: props.ocrData,
    modeOfPayment: props.modeOfPayment,
    paymentModesMap: props.paymentModesMap,
    validateReference: props.validateReference,
    getBankName: props.getBankName,
  })
}
